/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser } from '../../../../util/data';
import { APPLY_FOR_INVITE_URL } from '../../../../util/applyForInvite';

import {
  AvatarLarge,
  ExternalLink,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';
import IconCard from '../../../../components/SavedCardDetails/IconCard/IconCard';

import heroImage from '../../../../assets/images/logoutbackground.png';
import logoImage from '../../../../assets/images/logoutlogo.png';
const CustomLinkComponent = ({ linkConfig, currentPage }) => {
  const { group, text, type, href, route } = linkConfig;
  const getCurrentPageClass = page => {
    const hasPageName = name => currentPage?.indexOf(name) === 0;
    const isCMSPage = pageId => hasPageName('CMSPage') && currentPage === `${page}:${pageId}`;
    const isInboxPage = tab => hasPageName('InboxPage') && currentPage === `${page}:${tab}`;
    const isCurrentPage = currentPage === page;

    return isCMSPage(route?.params?.pageId) || isInboxPage(route?.params?.tab) || isCurrentPage
      ? css.currentPage
      : null;
  };

  // Note: if the config contains 'route' keyword,
  // then in-app linking config has been resolved already.
  if (type === 'internal' && route) {
    // Internal link
    const { name, params, to } = route || {};
    const className = classNames(css.navigationLink, getCurrentPageClass(name));
    return (
      <NamedLink name={name} params={params} to={to} className={className}>
        <span className={css.menuItemBorder} />
        {text}
      </NamedLink>
    );
  }
  return (
    <ExternalLink href={href} className={css.navigationLink}>
      <span className={css.menuItemBorder} />
      {text}
    </ExternalLink>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUserHasListings,
    currentUser,
    Count,
    customLinks,
    onLogout,
  } = props;

  const user = ensureCurrentUser(currentUser);
  const userId = user && user.id && user.id.uuid

  const extraLinks = customLinks.map(linkConfig => {
    return (
      <CustomLinkComponent
        key={linkConfig.text}
        linkConfig={linkConfig}
        currentPage={currentPage}
      />
    );
  });

  if (!isAuthenticated) {
    const signup = (
      <ExternalLink
        className={css.signupLink}
        href={APPLY_FOR_INVITE_URL}
      >
        <FormattedMessage id="AuthenticationPage.signupLinkText" />
      </ExternalLink>

    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ login, signup }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            {/* <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            /> */}
            <div className={css.card}>

              {/* TOP IMAGE */}
              <div className={css.imageWrapper}>
                <img
                  src={heroImage}
                  alt="Hero"
                  className={css.heroImage}
                />

                {/* LOGO */}
                <div className={css.logoWrapper}>
                  <img
                    src={logoImage}
                    alt="Logo"
                    className={css.logo}
                  />
                </div>
              </div>

              {/* CONTENT */}
              <div className={css.content}>
                <h1 className={css.title}>
                  Hi there, we’re so <br />
                  glad you’re here.
                </h1>
                <a href="/login" className={css.outlineButton}>
                  Log In
                </a>

                <a href={APPLY_FOR_INVITE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.primaryButton}>
                  Apply For Invitation
                </a>
              </div>

            </div>
          </div>

          <div className={css.customLinksWrapper}>{extraLinks}</div>

          {/* <div className={css.spacer} /> */}
        </div>
        {/* <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div> */}
      </div>
    );
  }

  const notificationCountBadge =
    Count > 0 ? (
      <NotificationBadge className={css.notificationBadge} count={Count} />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };
  const inboxTab = currentUserHasListings ? 'sales' : 'orders';
  // Build Mighty SSO authorize URL. If on localhost, call the dev API server (default 3500).
  const computeApiBase = () => {
    if (typeof window !== 'undefined') {
      const isLocalhost = window.location.hostname === 'localhost';
      if (isLocalhost) {
        const devPort = process.env.REACT_APP_DEV_API_SERVER_PORT || '3500';
        return `http://localhost:${devPort}`;
      }
    }
    return '';
  };
  const apiBase = computeApiBase();
  const redirectUri = encodeURIComponent('https://community.gfhr.co/auth/custom_sso/callback');
  const communityAuthorizeHref = `${apiBase}/api/oauth/authorize?response_type=code&client_id=gfhr_community&redirect_uri=${redirectUri}&scope=email`;

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.contentLogin}>
        {/* <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span> */}
        {/* <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton> */}



        {/* {userId ? (
          <NamedLink
            className={css.navigationLink}
            name="InviteFamiliesPage"
            params={{ id: userId }}
          >
            <FormattedMessage id="InviteFamiliesPage.heading" />

          </NamedLink>
        ) : null} */}
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
          name="ProfileSettingsPage"
        >
          <div className={css.linkContent}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 13.5C3 12.7044 3.31607 11.9413 3.87868 11.3787C4.44129 10.8161 5.20435 10.5 6 10.5H12C12.7956 10.5 13.5587 10.8161 14.1213 11.3787C14.6839 11.9413 15 12.7044 15 13.5C15 13.8978 14.842 14.2794 14.5607 14.5607C14.2794 14.842 13.8978 15 13.5 15H4.5C4.10218 15 3.72064 14.842 3.43934 14.5607C3.15804 14.2794 3 13.8978 3 13.5Z" stroke="black" stroke-width="1.5" stroke-linejoin="round" fill="none" />
              <path d="M9 7.5C10.2426 7.5 11.25 6.49264 11.25 5.25C11.25 4.00736 10.2426 3 9 3C7.75736 3 6.75 4.00736 6.75 5.25C6.75 6.49264 7.75736 7.5 9 7.5Z" stroke="black" stroke-width="1.5" fill="none" />
            </svg>

            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </div>
        </NamedLink>
        <NamedLink
          className={classNames(css.inbox, currentPageClass(`InboxPage:${inboxTab}`))}
          name="InboxPage"
          params={{ tab: inboxTab }}
        >
          <div className={css.linkContent}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_7596_3447)">
                <path d="M5.25 6.75H12M5.25 9.75H12M5.25 12.75H9M16.5 9C16.5 13.1422 13.1422 16.5 9 16.5H1.5V9C1.5 4.85775 4.85775 1.5 9 1.5C13.1422 1.5 16.5 4.85775 16.5 9Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill='none' />
              </g>
              <defs>
                <clipPath id="clip0_7596_3447">
                  <rect width="18" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </div>
        </NamedLink>
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
          name="ManageListingsPage"
        >
          <div className={css.linkContent}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11.25 15.75V9.75C11.25 9.55109 11.171 9.36032 11.0303 9.21967C10.8897 9.07902 10.6989 9 10.5 9H7.5C7.30109 9 7.11032 9.07902 6.96967 9.21967C6.82902 9.36032 6.75 9.55109 6.75 9.75V15.75" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
              <path d="M2.25 7.49999C2.24995 7.28179 2.2975 7.06621 2.38934 6.86828C2.48118 6.67035 2.6151 6.49484 2.78175 6.35399L8.03175 1.85399C8.30249 1.62517 8.64552 1.49963 9 1.49963C9.35448 1.49963 9.69751 1.62517 9.96825 1.85399L15.2183 6.35399C15.3849 6.49484 15.5188 6.67035 15.6107 6.86828C15.7025 7.06621 15.7501 7.28179 15.75 7.49999V14.25C15.75 14.6478 15.592 15.0293 15.3107 15.3107C15.0294 15.592 14.6478 15.75 14.25 15.75H3.75C3.35218 15.75 2.97064 15.592 2.68934 15.3107C2.40804 15.0293 2.25 14.6478 2.25 14.25V7.49999Z" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none" />
            </svg>

            <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
          </div>
        </NamedLink>
      {userId &&  <NamedLink
          className={css.inbox} name="FavoritesPage" params={{ id: userId }}
        >
          <div className={css.linkContent}>
            <svg xmlns="http://www.w3.org/2000/svg" width="17" height="15" viewBox="0 0 17 15" fill="none">
              <path d="M15.7498 5.1465C15.7524 6.30056 15.3078 7.41075 14.5093 8.244C12.6786 10.1422 10.9026 12.1215 9.00431 13.95C8.56856 14.3625 7.87781 14.3475 7.46156 13.9163L1.99031 8.24475C0.336562 6.53025 0.336562 3.76275 1.99031 2.049C2.38117 1.63875 2.85127 1.31214 3.37211 1.08898C3.89294 0.865819 4.45368 0.75075 5.02031 0.75075C5.58695 0.75075 6.14768 0.865819 6.66852 1.08898C7.18936 1.31214 7.65946 1.63875 8.05031 2.049L8.24981 2.2545L8.44856 2.049C8.83989 1.63931 9.31006 1.31302 9.83078 1.08978C10.3515 0.866543 10.912 0.750963 11.4786 0.75C12.6186 0.75 13.7083 1.218 14.5086 2.049C15.3073 2.88213 15.7522 3.99233 15.7498 5.1465Z" stroke="black" stroke-width="1.5" stroke-linejoin="round" fill='none' />
            </svg>
            <span>Saved Homes</span>
          </div>
        </NamedLink>}
        <NamedLink
          className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
          name="AccountSettingsPage"
        >
          <div className={css.linkContent}>
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9 6.1875C8.25408 6.1875 7.53871 6.48382 7.01126 7.01126C6.48382 7.53871 6.1875 8.25408 6.1875 9C6.1875 9.74592 6.48382 10.4613 7.01126 10.9887C7.53871 11.5162 8.25408 11.8125 9 11.8125C9.74592 11.8125 10.4613 11.5162 10.9887 10.9887C11.5162 10.4613 11.8125 9.74592 11.8125 9C11.8125 8.25408 11.5162 7.53871 10.9887 7.01126C10.4613 6.48382 9.74592 6.1875 9 6.1875ZM7.3125 9C7.3125 8.55245 7.49029 8.12323 7.80676 7.80676C8.12323 7.49029 8.55245 7.3125 9 7.3125C9.44755 7.3125 9.87678 7.49029 10.1932 7.80676C10.5097 8.12323 10.6875 8.55245 10.6875 9C10.6875 9.44755 10.5097 9.87678 10.1932 10.1932C9.87678 10.5097 9.44755 10.6875 9 10.6875C8.55245 10.6875 8.12323 10.5097 7.80676 10.1932C7.49029 9.87678 7.3125 9.44755 7.3125 9Z" fill="black" />
              <path fill-rule="evenodd" clip-rule="evenodd" d="M8.98133 0.9375C8.64758 0.9375 8.36933 0.9375 8.14132 0.9525C7.90823 0.961887 7.67834 1.01005 7.46108 1.095C7.21076 1.19857 6.9833 1.35043 6.79169 1.54192C6.60008 1.7334 6.44806 1.96076 6.34433 2.211C6.23558 2.4735 6.20633 2.751 6.19433 3.0525C6.19299 3.16274 6.16374 3.27084 6.10931 3.36671C6.05488 3.46258 5.97705 3.5431 5.88308 3.60075C5.78619 3.65349 5.67752 3.68081 5.56722 3.68015C5.45691 3.6795 5.34857 3.65089 5.25233 3.597C4.98533 3.456 4.73033 3.34275 4.44833 3.30525C4.17978 3.26993 3.90691 3.28784 3.6453 3.35798C3.38368 3.42811 3.13844 3.54909 2.92358 3.714C2.74162 3.85986 2.58523 4.035 2.46083 4.23225C2.33333 4.42275 2.19383 4.6635 2.02733 4.95225L2.00858 4.98525C1.84133 5.274 1.70258 5.51475 1.60208 5.72025C1.49708 5.93475 1.41458 6.14625 1.38458 6.38025C1.31303 6.92249 1.45978 7.47095 1.79258 7.905C1.96583 8.13075 2.19158 8.295 2.44658 8.4555C2.54155 8.51181 2.62068 8.59131 2.67656 8.68654C2.73244 8.78177 2.76324 8.88962 2.76608 9C2.76324 9.11038 2.73244 9.21823 2.67656 9.31346C2.62068 9.40869 2.54155 9.48819 2.44658 9.5445C2.19158 9.705 1.96658 9.86925 1.79258 10.095C1.62766 10.3099 1.50669 10.5551 1.43655 10.8167C1.36642 11.0783 1.3485 11.3512 1.38383 11.6198C1.41458 11.8538 1.49633 12.0652 1.60133 12.2797C1.70258 12.4852 1.84133 12.726 2.00858 13.0148L2.02733 13.0477C2.19383 13.3365 2.33333 13.5773 2.46083 13.7677C2.59358 13.965 2.73608 14.1428 2.92358 14.2853C3.13838 14.4503 3.3836 14.5714 3.64522 14.6417C3.90684 14.7119 4.17973 14.73 4.44833 14.6947C4.73033 14.6573 4.98533 14.5448 5.25233 14.403C5.34848 14.3492 5.45668 14.3207 5.56684 14.3201C5.677 14.3194 5.78554 14.3466 5.88233 14.3993C5.97706 14.4562 6.05558 14.5365 6.11034 14.6325C6.16511 14.7285 6.19429 14.837 6.19508 14.9475C6.20633 15.249 6.23558 15.5265 6.34508 15.789C6.44864 16.0393 6.60051 16.2668 6.79199 16.4584C6.98348 16.65 7.21083 16.802 7.46108 16.9058C7.67858 16.9958 7.90357 17.031 8.14132 17.0467C8.36933 17.0625 8.64758 17.0625 8.98133 17.0625H9.01883C9.35257 17.0625 9.63083 17.0625 9.85883 17.0475C10.0973 17.031 10.3216 16.9958 10.5391 16.905C10.7894 16.8014 11.0168 16.6496 11.2085 16.4581C11.4001 16.2666 11.5521 16.0392 11.6558 15.789C11.7646 15.5265 11.7938 15.249 11.8058 14.9475C11.807 14.8371 11.8362 14.7289 11.8907 14.6329C11.9451 14.5369 12.023 14.4562 12.1171 14.3985C12.214 14.3459 12.3227 14.3187 12.433 14.3195C12.5433 14.3203 12.6516 14.349 12.7478 14.403C13.0148 14.544 13.2698 14.6572 13.5518 14.694C14.0941 14.7655 14.6425 14.6188 15.0766 14.286C15.2641 14.142 15.4066 13.965 15.5393 13.7677C15.6668 13.5773 15.8063 13.3365 15.9728 13.0477L15.9916 13.0148C16.1588 12.726 16.2976 12.4852 16.3981 12.2797C16.5031 12.0652 16.5856 11.853 16.6156 11.6198C16.6871 11.0775 16.5404 10.529 16.2076 10.095C16.0343 9.86925 15.8086 9.705 15.5536 9.5445C15.4586 9.48819 15.3795 9.40869 15.3236 9.31346C15.2677 9.21823 15.2369 9.11038 15.2341 9C15.2341 8.7915 15.3481 8.5845 15.5536 8.4555C15.8086 8.295 16.0336 8.13075 16.2076 7.905C16.3725 7.69014 16.4935 7.4449 16.5636 7.18328C16.6337 6.92166 16.6516 6.64879 16.6163 6.38025C16.581 6.1497 16.5075 5.92664 16.3988 5.72025C16.2707 5.47108 16.1349 5.22596 15.9916 4.98525L15.9728 4.95225C15.8358 4.70781 15.6913 4.46769 15.5393 4.23225C15.4149 4.03527 15.2585 3.86038 15.0766 3.71475C14.8618 3.54971 14.6166 3.42859 14.3549 3.35833C14.0933 3.28807 13.8204 3.27003 13.5518 3.30525C13.2698 3.34275 13.0148 3.45525 12.7478 3.597C12.6517 3.65076 12.5435 3.67929 12.4333 3.67995C12.3231 3.6806 12.2146 3.65336 12.1178 3.60075C12.0236 3.54328 11.9454 3.46284 11.8907 3.36696C11.8361 3.27107 11.8066 3.16288 11.8051 3.0525C11.7938 2.751 11.7646 2.4735 11.6551 2.211C11.5515 1.96069 11.3996 1.73323 11.2082 1.54161C11.0167 1.35 10.7893 1.19799 10.5391 1.09425C10.3216 1.00425 10.0966 0.969 9.85883 0.95325C9.63083 0.9375 9.35257 0.9375 9.01883 0.9375H8.98133ZM7.89158 2.13375C7.94933 2.10975 8.03708 2.088 8.21782 2.07525C8.40308 2.0625 8.64308 2.0625 9.00008 2.0625C9.35708 2.0625 9.59708 2.0625 9.78233 2.07525C9.96308 2.088 10.0508 2.10975 10.1086 2.13375C10.3388 2.229 10.5211 2.41125 10.6163 2.6415C10.6463 2.7135 10.6711 2.82675 10.6808 3.0945C10.7033 3.6885 11.0101 4.26075 11.5546 4.575C12.0991 4.88925 12.7478 4.869 13.2736 4.5915C13.5106 4.46625 13.6208 4.431 13.6988 4.42125C13.9452 4.38869 14.1945 4.45532 14.3918 4.6065C14.4413 4.64475 14.5043 4.71 14.6056 4.86C14.7098 5.0145 14.8298 5.22225 15.0083 5.53125C15.1868 5.84025 15.3061 6.04875 15.3878 6.21525C15.4681 6.378 15.4928 6.465 15.5003 6.52725C15.5329 6.77367 15.4663 7.02294 15.3151 7.22025C15.2671 7.2825 15.1816 7.3605 14.9551 7.503C14.4511 7.8195 14.1091 8.3715 14.1091 9C14.1091 9.6285 14.4511 10.1805 14.9551 10.497C15.1816 10.6395 15.2671 10.7175 15.3151 10.7797C15.4666 10.977 15.5326 11.226 15.5003 11.4727C15.4928 11.535 15.4673 11.6228 15.3878 11.7847C15.3061 11.952 15.1868 12.1597 15.0083 12.4688C14.8298 12.7778 14.7091 12.9855 14.6056 13.14C14.5043 13.29 14.4413 13.3552 14.3918 13.3935C14.1945 13.5447 13.9452 13.6113 13.6988 13.5787C13.6208 13.569 13.5113 13.5338 13.2736 13.4085C12.7486 13.131 12.0991 13.11 11.5546 13.4242C11.0101 13.7385 10.7033 14.3115 10.6808 14.9055C10.6711 15.1733 10.6463 15.2865 10.6163 15.3585C10.5692 15.4723 10.5001 15.5758 10.413 15.6629C10.3259 15.75 10.2224 15.8191 10.1086 15.8663C10.0508 15.8903 9.96308 15.912 9.78233 15.9247C9.59708 15.9375 9.35708 15.9375 9.00008 15.9375C8.64308 15.9375 8.40308 15.9375 8.21782 15.9247C8.03708 15.912 7.94933 15.8903 7.89158 15.8663C7.77773 15.8191 7.67428 15.75 7.58716 15.6629C7.50004 15.5758 7.43094 15.4723 7.38383 15.3585C7.35383 15.2865 7.32908 15.1733 7.31933 14.9055C7.29683 14.3115 6.99008 13.7393 6.44558 13.425C5.90108 13.1107 5.25233 13.131 4.72658 13.4085C4.48958 13.5338 4.37933 13.569 4.30133 13.5787C4.0549 13.6113 3.80563 13.5447 3.60833 13.3935C3.55883 13.3552 3.49583 13.29 3.39458 13.14C3.25349 12.9204 3.11917 12.6966 2.99183 12.4688C2.81333 12.1597 2.69408 11.9513 2.61233 11.7847C2.53208 11.622 2.50733 11.535 2.49983 11.4727C2.46726 11.2263 2.5339 10.9771 2.68508 10.7797C2.73308 10.7175 2.81858 10.6395 3.04508 10.497C3.54908 10.1805 3.89108 9.6285 3.89108 9C3.89108 8.3715 3.54908 7.8195 3.04508 7.503C2.81858 7.3605 2.73308 7.2825 2.68508 7.22025C2.5339 7.02294 2.46726 6.77367 2.49983 6.52725C2.50733 6.465 2.53283 6.37725 2.61233 6.21525C2.69408 6.048 2.81333 5.84025 2.99183 5.53125C3.17033 5.22225 3.29108 5.0145 3.39458 4.86C3.49583 4.71 3.55883 4.64475 3.60833 4.6065C3.80563 4.45532 4.0549 4.38869 4.30133 4.42125C4.37933 4.431 4.48883 4.46625 4.72658 4.5915C5.25158 4.869 5.90108 4.89 6.44558 4.575C6.99008 4.26075 7.29683 3.6885 7.31933 3.0945C7.32908 2.82675 7.35383 2.7135 7.38383 2.6415C7.47908 2.41125 7.66133 2.229 7.89158 2.13375Z" fill="black" />
            </svg>

            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </div>
        </NamedLink>

        <div className={css.accountLinksWrapper}>

          {/* <NamedLink className={css.inbox} name="SearchPage">
            <FormattedMessage id="TopbarDesktop.SearchPage" />
          </NamedLink> */}


          {/* <NamedLink className={css.navigationLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink> */}
          <ExternalLink
            className={css.navigationLink}
            href={communityAuthorizeHref}
          >
            <div className={css.linkContent}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9 2.25C8.77839 2.25 8.55896 2.29365 8.35422 2.37845C8.14948 2.46326 7.96346 2.58756 7.80676 2.74426C7.65006 2.90096 7.52576 3.08698 7.44095 3.29172C7.35615 3.49646 7.3125 3.71589 7.3125 3.9375C7.3125 4.15911 7.35615 4.37854 7.44095 4.58328C7.52576 4.78802 7.65006 4.97404 7.80676 5.13074C7.96346 5.28744 8.14948 5.41174 8.35422 5.49655C8.55896 5.58135 8.77839 5.625 9 5.625C9.44755 5.625 9.87678 5.44721 10.1932 5.13074C10.5097 4.81428 10.6875 4.38505 10.6875 3.9375C10.6875 3.48995 10.5097 3.06072 10.1932 2.74426C9.87678 2.42779 9.44755 2.25 9 2.25ZM6.1875 3.9375C6.1875 3.19158 6.48382 2.47621 7.01126 1.94876C7.53871 1.42132 8.25408 1.125 9 1.125C9.74592 1.125 10.4613 1.42132 10.9887 1.94876C11.5162 2.47621 11.8125 3.19158 11.8125 3.9375C11.8125 4.68342 11.5162 5.39879 10.9887 5.92624C10.4613 6.45368 9.74592 6.75 9 6.75C8.25408 6.75 7.53871 6.45368 7.01126 5.92624C6.48382 5.39879 6.1875 4.68342 6.1875 3.9375ZM4.5 9.5625C4.50075 9.357 4.52175 9.15825 4.563 8.96625L2.376 9.55125C2.1619 9.60868 1.96122 9.70773 1.78541 9.84273C1.60961 9.97774 1.46212 10.1461 1.35138 10.3381C1.24064 10.5301 1.16882 10.7421 1.14001 10.9618C1.11121 11.1816 1.12598 11.4049 1.1835 11.619L1.62 13.2491C1.73736 13.6865 1.94154 14.0958 2.22039 14.4527C2.49925 14.8095 2.84706 15.1066 3.24312 15.3262C3.63919 15.5458 4.0754 15.6834 4.52577 15.7309C4.97614 15.7784 5.43147 15.7348 5.86462 15.6026C5.56375 15.3108 5.30512 14.9783 5.09625 14.6149C4.56629 14.666 4.03535 14.5276 3.59784 14.2242C3.16032 13.9208 2.84458 13.472 2.70675 12.9577L2.26912 11.3265C2.23066 11.1824 2.251 11.0289 2.32569 10.8998C2.40037 10.7707 2.52327 10.6765 2.66738 10.638L4.5 10.1475V9.5625ZM12.2467 15.6353L12.1343 15.6038C12.4346 15.3112 12.6945 14.9794 12.9038 14.6149C13.4337 14.666 13.9647 14.5276 14.4022 14.2242C14.8397 13.9208 15.1554 13.472 15.2933 12.9577L15.7298 11.3265C15.7683 11.1825 15.7481 11.0292 15.6736 10.9001C15.5992 10.771 15.4765 10.6768 15.3326 10.638L13.5 10.1475V9.5625C13.4993 9.357 13.4783 9.15825 13.437 8.96625L15.6229 9.55125C15.8371 9.60856 16.0379 9.70752 16.2138 9.84247C16.3897 9.97743 16.5373 10.1457 16.6482 10.3378C16.7591 10.5298 16.831 10.7418 16.8598 10.9616C16.8887 11.1815 16.874 11.4049 16.8165 11.619L16.38 13.2491C16.148 14.1134 15.5823 14.8502 14.8072 15.2974C14.0321 15.7446 13.1111 15.8669 12.2467 15.6353ZM2.25 5.625C2.25 5.32663 2.36853 5.04048 2.5795 4.8295C2.79048 4.61853 3.07663 4.5 3.375 4.5C3.67337 4.5 3.95952 4.61853 4.1705 4.8295C4.38147 5.04048 4.5 5.32663 4.5 5.625C4.5 5.92337 4.38147 6.20952 4.1705 6.4205C3.95952 6.63147 3.67337 6.75 3.375 6.75C3.07663 6.75 2.79048 6.63147 2.5795 6.4205C2.36853 6.20952 2.25 5.92337 2.25 5.625ZM3.375 3.375C2.77826 3.375 2.20597 3.61205 1.78401 4.03401C1.36205 4.45597 1.125 5.02826 1.125 5.625C1.125 6.22174 1.36205 6.79403 1.78401 7.21599C2.20597 7.63795 2.77826 7.875 3.375 7.875C3.97174 7.875 4.54403 7.63795 4.96599 7.21599C5.38795 6.79403 5.625 6.22174 5.625 5.625C5.625 5.02826 5.38795 4.45597 4.96599 4.03401C4.54403 3.61205 3.97174 3.375 3.375 3.375ZM14.625 4.5C14.3266 4.5 14.0405 4.61853 13.8295 4.8295C13.6185 5.04048 13.5 5.32663 13.5 5.625C13.5 5.92337 13.6185 6.20952 13.8295 6.4205C14.0405 6.63147 14.3266 6.75 14.625 6.75C14.9234 6.75 15.2095 6.63147 15.4205 6.4205C15.6315 6.20952 15.75 5.92337 15.75 5.625C15.75 5.32663 15.6315 5.04048 15.4205 4.8295C15.2095 4.61853 14.9234 4.5 14.625 4.5ZM12.375 5.625C12.375 5.02826 12.6121 4.45597 13.034 4.03401C13.456 3.61205 14.0283 3.375 14.625 3.375C15.2217 3.375 15.794 3.61205 16.216 4.03401C16.6379 4.45597 16.875 5.02826 16.875 5.625C16.875 6.22174 16.6379 6.79403 16.216 7.21599C15.794 7.63795 15.2217 7.875 14.625 7.875C14.0283 7.875 13.456 7.63795 13.034 7.21599C12.6121 6.79403 12.375 6.22174 12.375 5.625ZM7.3125 7.875C6.86495 7.875 6.43572 8.05279 6.11926 8.36926C5.80279 8.68572 5.625 9.11495 5.625 9.5625V12.375C5.625 13.2701 5.98058 14.1286 6.61351 14.7615C7.24645 15.3944 8.10489 15.75 9 15.75C9.89511 15.75 10.7536 15.3944 11.3865 14.7615C12.0194 14.1286 12.375 13.2701 12.375 12.375V9.5625C12.375 9.11495 12.1972 8.68572 11.8807 8.36926C11.5643 8.05279 11.1351 7.875 10.6875 7.875H7.3125ZM6.75 9.5625C6.75 9.41332 6.80926 9.27024 6.91475 9.16475C7.02024 9.05926 7.16332 9 7.3125 9H10.6875C10.8367 9 10.9798 9.05926 11.0852 9.16475C11.1907 9.27024 11.25 9.41332 11.25 9.5625V12.375C11.25 12.9717 11.0129 13.544 10.591 13.966C10.169 14.3879 9.59674 14.625 9 14.625C8.40326 14.625 7.83097 14.3879 7.40901 13.966C6.98705 13.544 6.75 12.9717 6.75 12.375V9.5625Z" fill="black" />
              </svg>

              <FormattedMessage id="TopbarDesktop.communityLink" />
            </div>
          </ExternalLink>
          <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
            <div className={css.linkContent}>

              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3.75 15.75C3.3375 15.75 2.9845 15.6033 2.691 15.3097C2.3975 15.0162 2.2505 14.663 2.25 14.25V3.75C2.25 3.3375 2.397 2.9845 2.691 2.691C2.985 2.3975 3.338 2.2505 3.75 2.25H9V3.75H3.75V14.25H9V15.75H3.75ZM12 12.75L10.9688 11.6625L12.8812 9.75H6.75V8.25H12.8812L10.9688 6.3375L12 5.25L15.75 9L12 12.75Z" fill="black" />
              </svg>
              <FormattedMessage id="TopbarMobileMenu.logoutLink" />
            </div>
          </InlineTextButton>
        </div>
        <div className={css.customLinksWrapper}>{extraLinks}</div>
        <div className={css.spacer} />
      </div>
      <div className={css.footer}>

        <NamedLink className={css.createNewListingLink} name="NewListingPage">
          <FormattedMessage id="TopbarMobileMenu.newListingLink" />
        </NamedLink>
      </div>
    </div>
  );
};

TopbarMobileMenu.defaultProps = { currentUser: null, currentPage: null };

const { bool, func, number, string } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
